import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    ShieldExclamationIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getFraudAlerts } from "../api/fraudSafetyApi";
import type {
    FraudAlertsQueryParams,
    FraudAlertType,
    FraudAlertSeverity,
    FraudAlertStatus,
} from "../types";

// ============================================================================
// Constants
// ============================================================================

const alertTypeOptions: { value: FraudAlertType | ""; label: string }[] = [
    { value: "", label: "All Types" },
    { value: "SUSPICIOUS_PAYMENT", label: "Suspicious Payment" },
    { value: "HIGH_CANCELLATION", label: "High Cancellation" },
    { value: "FAKE_BOOKING", label: "Fake Booking" },
    { value: "PRICING_ANOMALY", label: "Pricing Anomaly" },
    { value: "GPS_INCONSISTENCY", label: "GPS Inconsistency" },
];

const severityOptions: { value: FraudAlertSeverity | ""; label: string }[] = [
    { value: "", label: "All Severity" },
    { value: "CRITICAL", label: "Critical" },
    { value: "HIGH", label: "High" },
    { value: "MEDIUM", label: "Medium" },
    { value: "LOW", label: "Low" },
];

const statusOptions: { value: FraudAlertStatus | ""; label: string }[] = [
    { value: "", label: "All Status" },
    { value: "NEW", label: "New" },
    { value: "UNDER_REVIEW", label: "Under Review" },
    { value: "RESOLVED", label: "Resolved" },
    { value: "FALSE_POSITIVE", label: "False Positive" },
    { value: "ESCALATED", label: "Escalated" },
];

const userTypeOptions: { value: "RIDER" | "DRIVER" | ""; label: string }[] = [
    { value: "", label: "All User Types" },
    { value: "RIDER", label: "Rider" },
    { value: "DRIVER", label: "Driver" },
];

// ============================================================================
// Badges
// ============================================================================

const SeverityBadge = ({ severity }: { severity: string }) => {
    const s = severity ?? "UNKNOWN";
    const styles: Record<string, string> = {
        CRITICAL: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        HIGH: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        MEDIUM: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        LOW: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
    };
    const dots: Record<string, string> = {
        CRITICAL: "bg-rose-500",
        HIGH: "bg-orange-500",
        MEDIUM: "bg-amber-500",
        LOW: "bg-sky-500",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${dots[s] || "bg-slate-400"}`} />
            {s}
        </span>
    );
};

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        NEW: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        UNDER_REVIEW: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        RESOLVED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FALSE_POSITIVE: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
        ESCALATED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s.replace("_", " ")}
        </span>
    );
};

const AlertTypeBadge = ({ type }: { type: string }) => {
    const styles: Record<string, string> = {
        SUSPICIOUS_PAYMENT: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
        HIGH_CANCELLATION: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        FAKE_BOOKING: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
        PRICING_ANOMALY: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        GPS_INCONSISTENCY: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[type] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {type?.replace(/_/g, " ") || "UNKNOWN"}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

// ============================================================================
// Component
// ============================================================================

export const AlertsRoute = () => {
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<FraudAlertsQueryParams>({
        page: 1,
        limit: 20,
        alertType: "",
        severity: "",
        status: "",
        userType: "",
    });

    const params: FraudAlertsQueryParams = {
        ...filters,
        alertType: filters.alertType || undefined,
        severity: filters.severity || undefined,
        status: filters.status || undefined,
        userType: filters.userType || undefined,
    };

    const { data, isLoading, error } = useQuery({
        queryKey: ["fraud-alerts", params],
        queryFn: () => getFraudAlerts(params),
    });

    const alerts = data?.data?.alerts ?? [];
    const currentPage = data?.data?.currentPage ?? 1;
    const totalPages = data?.data?.totalPages ?? 1;
    const totalElements = data?.data?.totalElements ?? 0;

    const filtered = search
        ? alerts.filter(
            (a) =>
                a.title?.toLowerCase().includes(search.toLowerCase()) ||
                a.userName?.toLowerCase().includes(search.toLowerCase()) ||
                a.userEmail?.toLowerCase().includes(search.toLowerCase()) ||
                a.alertId?.toLowerCase().includes(search.toLowerCase())
        )
        : alerts;

    // ========================================================================
    // Pagination
    // ========================================================================

    const handlePrev = () => {
        if (currentPage > 1) setFilters((p) => ({ ...p, page: (p.page ?? 1) - 1 }));
    };
    const handleNext = () => {
        if (currentPage < totalPages) setFilters((p) => ({ ...p, page: (p.page ?? 1) + 1 }));
    };

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">Failed to load fraud alerts</h3>
                <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{(error as Error).message}</p>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search alerts…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)}>
                    <FunnelIcon className="mr-1.5 h-4 w-4" /> Filters
                </Button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        <select
                            value={filters.alertType ?? ""}
                            onChange={(e) => setFilters((p) => ({ ...p, alertType: e.target.value as FraudAlertType | "", page: 1 }))}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        >
                            {alertTypeOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        <select
                            value={filters.severity ?? ""}
                            onChange={(e) => setFilters((p) => ({ ...p, severity: e.target.value as FraudAlertSeverity | "", page: 1 }))}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        >
                            {severityOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        <select
                            value={filters.status ?? ""}
                            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value as FraudAlertStatus | "", page: 1 }))}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        >
                            {statusOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        <select
                            value={filters.userType ?? ""}
                            onChange={(e) => setFilters((p) => ({ ...p, userType: e.target.value as "RIDER" | "DRIVER" | "", page: 1 }))}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        >
                            {userTypeOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/50">
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Alert</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Type</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Severity</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Status</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">User</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Risk</th>
                                <th className="whitespace-nowrap px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-12 text-center">
                                        <ShieldExclamationIcon className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
                                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No fraud alerts found</p>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((alert) => (
                                    <tr
                                        key={alert.alertId}
                                        onClick={() => navigate(`/fraud-safety/alerts/${alert.alertId}`)}
                                        className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="max-w-[200px]">
                                                <p className="truncate font-medium text-slate-900 dark:text-white">{alert.title}</p>
                                                <p className="truncate text-xs text-slate-500 dark:text-slate-400">{alert.description}</p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <AlertTypeBadge type={alert.alertType} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <SeverityBadge severity={alert.severity} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={alert.status} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div>
                                                <p className="text-sm text-slate-900 dark:text-white">{alert.userName}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">{alert.userType}</p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-sm font-semibold ${alert.riskScore >= 70 ? "text-rose-600" : alert.riskScore >= 50 ? "text-orange-600" : alert.riskScore >= 30 ? "text-amber-600" : "text-emerald-600"}`}>
                                                {alert.riskScore}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                                            {formatDate(alert.createdAt)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 dark:border-slate-800">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        {totalElements} alert{totalElements !== 1 ? "s" : ""} total
                    </p>
                    <div className="flex items-center gap-2">
                        <Button variant="secondary" size="sm" onClick={handlePrev} disabled={currentPage <= 1}>
                            <ChevronLeftIcon className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-slate-600 dark:text-slate-300">
                            {currentPage} / {totalPages}
                        </span>
                        <Button variant="secondary" size="sm" onClick={handleNext} disabled={currentPage >= totalPages}>
                            <ChevronRightIcon className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
